import type { MinerSpec } from './minerTypes';

// Cache for miners data
let minersCache: MinerSpec[] | null = null;

// Export the miners array for use in the import script
export const miners: MinerSpec[] = [
  // Bitcoin Miners - 2025 (Predicted)
  {
    name: "Antminer S22 XP",
    model: "S22 XP",
    algorithm: "SHA-256",
    hashrate: 430,
    hashrateUnit: "TH/s",
    power: 5800,
    coin: "BTC",
    manufacturer: "Bitmain",
    releaseYear: 2025,
    status: 'upcoming',
    expectedRelease: 'Q2 2025',
    supportedCoins: ["BTC", "BCH"]
  },
  {
    name: "Whatsminer M60S++",
    model: "M60S++",
    algorithm: "SHA-256",
    hashrate: 380,
    hashrateUnit: "TH/s",
    power: 5600,
    coin: "BTC",
    manufacturer: "MicroBT",
    releaseYear: 2025,
    status: 'upcoming',
    expectedRelease: 'Q1 2025',
    supportedCoins: ["BTC", "BCH"]
  },
  // Current Bitcoin Miners
  {
    name: "Antminer S21 XP Hyd",
    model: "S21 XP Hyd",
    algorithm: "SHA-256",
    hashrate: 335,
    hashrateUnit: "TH/s",
    power: 5390,
    coin: "BTC",
    manufacturer: "Bitmain",
    releaseYear: 2024,
    status: 'current',
    efficiency: 0.062,
    noise: 75,
    cooling: "Hydro",
    interface: "Ethernet",
    psu: "Built-in",
    voltage: "200-240V",
    temperature: { min: 5, max: 45 },
    humidity: { min: 5, max: 95 },
    supportedCoins: ["BTC", "BCH"]
  },
  {
    name: "Antminer S21",
    model: "S21",
    algorithm: "SHA-256",
    hashrate: 200,
    hashrateUnit: "TH/s",
    power: 3450,
    coin: "BTC",
    manufacturer: "Bitmain",
    releaseYear: 2024,
    status: 'current',
    supportedCoins: ["BTC", "BCH"]
  },
  {
    name: "Whatsminer M56S++",
    model: "M56S++",
    algorithm: "SHA-256",
    hashrate: 230,
    hashrateUnit: "TH/s",
    power: 4968,
    coin: "BTC",
    manufacturer: "MicroBT",
    releaseYear: 2023,
    status: 'current',
    supportedCoins: ["BTC", "BCH"]
  },
  {
    name: "Antminer S19K Pro",
    model: "S19K Pro",
    algorithm: "SHA-256",
    hashrate: 136,
    hashrateUnit: "TH/s",
    power: 3010,
    coin: "BTC",
    manufacturer: "Bitmain",
    releaseYear: 2023,
    status: 'current',
    supportedCoins: ["BTC", "BCH"]
  },

  // Litecoin Miners - 2025 (Predicted)
  {
    name: "Antminer L10 Pro",
    model: "L10 Pro",
    algorithm: "Scrypt",
    hashrate: 25.0,
    hashrateUnit: "GH/s",
    power: 3800,
    coin: "LTC",
    manufacturer: "Bitmain",
    releaseYear: 2025,
    status: 'upcoming',
    expectedRelease: 'Q2 2025',
    supportedCoins: ["LTC", "DOGE"]
  },
  // Current Litecoin Miners
  {
    name: "Antminer L9 Pro",
    model: "L9 Pro",
    algorithm: "Scrypt",
    hashrate: 17.5,
    hashrateUnit: "GH/s",
    power: 3600,
    coin: "LTC",
    manufacturer: "Bitmain",
    releaseYear: 2024,
    status: 'current',
    supportedCoins: ["LTC", "DOGE"]
  },
  {
    name: "Antminer L7 Pro",
    model: "L7 Pro",
    algorithm: "Scrypt",
    hashrate: 14.0,
    hashrateUnit: "GH/s",
    power: 3430,
    coin: "LTC",
    manufacturer: "Bitmain",
    releaseYear: 2023,
    status: 'current',
    supportedCoins: ["LTC", "DOGE"]
  },
  {
    name: "Goldshell LT6 Pro",
    model: "LT6 Pro",
    algorithm: "Scrypt",
    hashrate: 12.0,
    hashrateUnit: "GH/s",
    power: 3300,
    coin: "LTC",
    manufacturer: "Goldshell",
    releaseYear: 2023,
    status: 'current',
    supportedCoins: ["LTC", "DOGE"]
  },

  // Ethereum Miners - 2025 (Predicted)
  {
    name: "Antminer E10",
    model: "E10",
    algorithm: "EtHash",
    hashrate: 6200,
    hashrateUnit: "MH/s",
    power: 3200,
    coin: "ETH",
    manufacturer: "Bitmain",
    releaseYear: 2025,
    status: 'upcoming',
    expectedRelease: 'Q2 2025',
    supportedCoins: ["ETH", "ETC"]
  },
  // Current Ethereum Miners
  {
    name: "Antminer E9 Pro+",
    model: "E9 Pro+",
    algorithm: "EtHash",
    hashrate: 4500,
    hashrateUnit: "MH/s",
    power: 2800,
    coin: "ETH",
    manufacturer: "Bitmain",
    releaseYear: 2024,
    status: 'current',
    supportedCoins: ["ETH", "ETC"]
  },
  {
    name: "iPollo V1 Ultra",
    model: "V1 Ultra",
    algorithm: "EtHash",
    hashrate: 3600,
    hashrateUnit: "MH/s",
    power: 2800,
    coin: "ETH",
    manufacturer: "iPollo",
    releaseYear: 2023,
    status: 'current',
    supportedCoins: ["ETH", "ETC"]
  },
  {
    name: "Jasminer X16-Q",
    model: "X16-Q",
    algorithm: "EtHash",
    hashrate: 2500,
    hashrateUnit: "MH/s",
    power: 1200,
    coin: "ETH",
    manufacturer: "Jasminer",
    releaseYear: 2023,
    status: 'current',
    supportedCoins: ["ETH", "ETC"]
  }
];

// Helper functions with caching
export function getMinersByAlgorithm(algorithm: string): MinerSpec[] {
  return (minersCache || miners).filter(miner => miner.algorithm === algorithm);
}

export function getMinersByCoin(coin: string): MinerSpec[] {
  return (minersCache || miners).filter(miner => 
    miner.coin === coin || miner.supportedCoins?.includes(coin)
  );
}

export function getMinerByModel(model: string): MinerSpec | undefined {
  return (minersCache || miners).find(miner => miner.model === model);
}

export function getAlgorithms(): string[] {
  return [...new Set((minersCache || miners).map(miner => miner.algorithm))].sort();
}

export function getSupportedCoins(): string[] {
  const coins = new Set<string>();
  (minersCache || miners).forEach(miner => {
    coins.add(miner.coin);
    miner.supportedCoins?.forEach(coin => coins.add(coin));
  });
  return [...coins].sort();
}

// Function to update cache
export function updateMinersCache(newMiners: MinerSpec[]) {
  minersCache = newMiners;
}

// Function to clear cache
export function clearMinersCache() {
  minersCache = null;
}